/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  useApproveSubscriptionPaymentMutation,
  usePendingOwnerSubscriptionPayments,
} from '@/hooks/usePlatformOwnerDetail'

type SubscriptionPlan = 'MONTHLY_STANDARD' | 'YEARLY_ECO' | 'LIFETIME_PREMIUM'

type SubscriptionPaymentStatus =
  | 'PENDING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED'

type SubscriptionPayment = {
  id: string
  ownerId: string
  plan: SubscriptionPlan
  amountCfa: number
  currency: string
  status: SubscriptionPaymentStatus
  createdAt: string
  paidAt?: string | null
  errorMessage?: string | null
}

const PLAN_LABELS: Record<SubscriptionPlan, string> = {
  MONTHLY_STANDARD: 'Standard',
  YEARLY_ECO: 'Eco',
  LIFETIME_PREMIUM: 'Premium',
}

function formatAmount(amount: number) {
  return `${new Intl.NumberFormat('fr-FR').format(amount)} CFA`
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('fr-FR')
}

export default function OwnerSubscriptionRequestsCard({
  ownerId,
}: {
  ownerId: string
}) {
  const {
    data = [],
    isLoading,
    isError,
  } = usePendingOwnerSubscriptionPayments(ownerId)

  console.log(data)

  const approveMut = useApproveSubscriptionPaymentMutation(ownerId)

  const pendingPayments = useMemo(
    () => (data as SubscriptionPayment[]).filter((p) => p.status === 'PENDING'),
    [data]
  )

  const handleApprove = async (paymentId: string) => {
    try {
      await approveMut.mutateAsync(paymentId)
      toast.success('Paiement validé et plan activé')
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? 'Impossible de valider le paiement'
      )
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Demandes d’abonnement</CardTitle>
      </CardHeader>

      <CardContent className='space-y-3'>
        {isLoading ? (
          <p className='text-sm text-muted-foreground'>Chargement...</p>
        ) : isError ? (
          <p className='text-sm text-destructive'>
            Impossible de charger les demandes d’abonnement.
          </p>
        ) : pendingPayments.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            Aucune demande en attente.
          </p>
        ) : (
          pendingPayments.map((payment) => (
            <div
              key={payment.id}
              className='flex flex-col gap-4 rounded-xl border p-4 md:flex-row md:items-center md:justify-between'
            >
              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <div className='font-medium'>
                    {PLAN_LABELS[payment.plan] ?? payment.plan}
                  </div>
                  <Badge variant='outline'>PENDING</Badge>
                </div>

                <div className='text-sm text-muted-foreground'>
                  Montant : {formatAmount(payment.amountCfa)}
                </div>

                <div className='text-xs text-muted-foreground'>
                  Demandé le {formatDate(payment.createdAt)}
                </div>

                {payment.errorMessage ? (
                  <div className='text-xs text-muted-foreground'>
                    Note : {payment.errorMessage}
                  </div>
                ) : null}
              </div>

              <div className='flex items-center gap-2'>
                <Button
                  onClick={() => handleApprove(payment.id)}
                  disabled={approveMut.isPending}
                >
                  {approveMut.isPending
                    ? 'Validation...'
                    : 'Valider le paiement'}
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  )
}
